import { useMemo } from 'react';
import { useUIStore } from '../store/uiStore';

/**
 * The one definition of what a Stripe card field looks like — the counterpart to
 * `fieldClassName` in shared/ui/formStyles.js, which does the same job for the native inputs
 * these fields sit between.
 *
 * Stripe Elements render in an iframe served from js.stripe.com, so neither the `dark` class on
 * <html> nor any `var(--token)` in our stylesheet reaches them: the only way to colour them is to
 * pass literal values in the `style` option. That is what this hook does — it reads the
 * `--stripe-*` tokens off <html> at render time and hands Stripe the resolved colours, so the
 * fields still follow the palette in theme.css rather than carrying a second, hand-kept copy of
 * it. Three components used to declare that copy verbatim, all three hardcoding a navy that was
 * unreadable on a dark panel.
 *
 * Re-resolves on a theme flip: `isDarkMode` re-renders the consumer, and react-stripe-js pushes a
 * changed `style` through `element.update()`, so mounted fields recolour in place without
 * remounting (a remount would wipe whatever the customer had typed).
 */

/* Used when the tokens cannot be read — a non-DOM test env, or a render before the stylesheet has
   applied. These are the light values verbatim, so the fallback is the light theme rather than an
   invented palette. */
const FALLBACK_COLORS = {
  color: '#2B3674',
  placeholder: 'rgba(26, 52, 73, 0.4)',
  invalid: '#E44F4F',
};

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

function readStripeColors() {
  try {
    const styles = getComputedStyle(document.documentElement);
    const read = (token, fallback) => styles.getPropertyValue(token).trim() || fallback;
    return {
      color: read('--stripe-fg', FALLBACK_COLORS.color),
      placeholder: read('--stripe-placeholder', FALLBACK_COLORS.placeholder),
      invalid: read('--stripe-invalid', FALLBACK_COLORS.invalid),
    };
  } catch {
    return FALLBACK_COLORS;
  }
}

/**
 * @param {object} [options]
 * @param {string} [options.fontSize]       Defaults to the 14px the card forms use.
 * @param {string} [options.letterSpacing]  Only the CVC re-verification field spaces its digits.
 * @returns {object} A Stripe Elements `style` object, stable between theme changes.
 */
export function useStripeElementStyle({ fontSize = '14px', letterSpacing } = {}) {
  const isDarkMode = useUIStore((state) => state.isDarkMode);

  // `isDarkMode` is a cache key, not an input — hence the disable, which is not a missing
  // dependency but a deliberate extra one. The read below resolves tokens against the class the
  // theme flips on <html>, so a flip is the only thing that changes its result, and nothing in
  // the DOM tells React that. Reading on every render instead would force a style recalc on each
  // keystroke in the form around the field, since a controlled input beside it re-renders this
  // component.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { color, placeholder, invalid } = useMemo(() => readStripeColors(), [isDarkMode]);

  // Memoized so the `style` prop keeps a stable identity across unrelated re-renders of the form
  // around it; react-stripe-js diffs the options object on every render.
  return useMemo(
    () => ({
      base: {
        fontSize,
        color,
        fontFamily: FONT_FAMILY,
        ...(letterSpacing ? { letterSpacing } : {}),
        '::placeholder': { color: placeholder },
      },
      invalid: { color: invalid },
    }),
    [fontSize, letterSpacing, color, placeholder, invalid]
  );
}
